import styled from 'styled-components';
import { useState, ChangeEvent } from 'react';
import api from '@/api/client';

export const ImageUploader = ({ onChange, value, handleDelete, acceptType = 'image', label }: { 
    onChange: (url: string) => void; 
    value: string; 
    handleDelete: () => void;
    acceptType?: 'image' | 'video';
    label?: string;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            const selectedFile = event.target.files[0];
            setFile(selectedFile);
            setUploading(true);

            try {
                // Upload file to S3 and get URL
                const formData = new FormData();
                formData.append('file', selectedFile);

                const response = await api.post('/upload_handler', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const uploadUrl = response.data.Url;
                onChange(uploadUrl); // Store URL instead of base64
                setUploading(false);
            } catch (error) {
                console.error('Upload failed:', error);
                setUploading(false);
                // Fallback to base64 if upload fails
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    onChange(base64String);
                };
                reader.readAsDataURL(selectedFile);
            }
        }
    };

    const accept = acceptType === 'video' ? 'video/*' : 'image/*';
    const defaultLabel = acceptType === 'video' ? 'Выбрать видео' : 'Выбрать изображение';
    const displayLabel = label || defaultLabel;
    
    // Check if media is loaded
    const isUrl = value && (value.startsWith('http://') || value.startsWith('https://'));
    const isBase64Image = value && value.startsWith('data:image');
    const hasMedia = file || value;

    return (
        <Container>
            {hasMedia && !uploading && (
                <MediaWrapper>
                    {acceptType === 'video' ? (
                        <VideoPlaceholder>
                            <VideoIcon>🎥</VideoIcon>
                            <VideoText>Видео загружено</VideoText>
                            <DeleteIcon className="delete-icon" onClick={handleDelete}>
                                &times;
                            </DeleteIcon>
                        </VideoPlaceholder>
                    ) : (
                        <>
                            {isUrl || isBase64Image ? (
                                <Image
                                    src={isUrl ? value : (file ? URL.createObjectURL(file) : value)}
                                    alt="Preview"
                                />
                            ) : null}
                            <DeleteIcon className="delete-icon" onClick={handleDelete}>
                                &times;
                            </DeleteIcon>
                        </>
                    )}
                </MediaWrapper>
            )}
            {uploading && (
                <UploadingText>Загрузка...</UploadingText>
            )}
            {!hasMedia && !uploading && (
                <Label>
                    {displayLabel}
                    <input type="file" accept={accept} style={{ display: "none" }} onChange={handleFileChange} />
                </Label>
            )}
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    display: inline-block;
`;

const MediaWrapper = styled.div`
    position: relative;
    &:hover .delete-icon {
        opacity: 1;
    }
`;

const Image = styled.img`
    max-width: 350px;
    max-height: 350px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
`;

const VideoPlaceholder = styled.div`
    width: 200px;
    height: 80px;
    background-color: #f5f5f5;
    border: 2px dashed #2358A2;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
`;

const VideoIcon = styled.div`
    font-size: 24px;
`;

const VideoText = styled.div`
    color: #2358A2;
    font-size: 14px;
    font-weight: 500;
`;

const DeleteIcon = styled.div`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.3s;
`;

const Label = styled.label`
    color: #2358A2;
    cursor: pointer;
    font-weight: 400;
    font-size: 14px;
`;

const UploadingText = styled.div`
    color: #2358A2;
    font-size: 14px;
    font-weight: 500;
`;
